import { useMemo, useEffect, useState } from 'react';
import { permissionsApi } from '../services/api';

/**
 * ─────────────────────────────────────────────────────────────
 * usePermissions — PermissionContext ka re-export
 *
 * - Codes PermissionContext se aate hain (cache-first + API refresh)
 * - P object DB se fetch hota hai (CreatePermission se jo bhi banao)
 * - ADMIN ko sab permissions automatically milti hain
 *
 * Usage:
 *   const { has, isAdmin } = usePermissions();
 *   has('LEAD_VIEW')          // ✅ string directly
 *   has('MERA_NAYA_CODE')     // ✅ DB mein add kiya to auto kaam karega
 * ─────────────────────────────────────────────────────────────
 */
// ✅ Ab ye sirf PermissionContext ko re-export karta hai — pehle yahan apna
//    alag localStorage logic tha jo context se out-of-sync ho jaata tha.
//    Public API wahi hai ({ has, isAdmin }), isliye jo 12 pages ise already
//    use karte hain unme kuch badalne ki zaroorat nahi.
export { usePermissions } from '../context/PermissionContext';

/**
 * ─────────────────────────────────────────────────────────────
 * useAllPermissions — DB se saari permissions fetch karta hai
 *
 * Usage:
 *   const { P, loading } = useAllPermissions();
 *   has(P.LEAD_VIEW)   // 'LEAD_VIEW'
 *   has(P.MERA_NAYA)   // 'MERA_NAYA' — DB mein add kiya to auto aayega
 * ─────────────────────────────────────────────────────────────
 */
export function useAllPermissions() {
  const [P, setP] = useState(() => {
    // ── Cache: localStorage mein stored permissions se P object banao ──
    try {
      const cached = localStorage.getItem('allPermissions');
      if (cached) {
        const perms = JSON.parse(cached);
        return buildP(perms);
      }
    } catch {}
    return {};
  });

  const [loading, setLoading] = useState(false);
  const [allPerms, setAllPerms] = useState([]);

  useEffect(() => {
    fetchAndBuildP();
  }, []);

  const fetchAndBuildP = async () => {
    try {
      setLoading(true);
      const data = await permissionsApi.getAllPermissions();
      if (Array.isArray(data) && data.length > 0) {
        // Cache karo taaki next reload pe instant mile
        localStorage.setItem('allPermissions', JSON.stringify(data));
        setAllPerms(data);
        setP(buildP(data));
      }
    } catch (err) {
      console.warn('useAllPermissions: fetch failed, using cache', err);
    } finally {
      setLoading(false);
    }
  };

  return { P, loading, allPerms, refetch: fetchAndBuildP };
}

/**
 * buildP — permission array se P object banao
 * Input:  [{ code: 'LEAD_VIEW', ... }, ...]
 * Output: { LEAD_VIEW: 'LEAD_VIEW', LEAD_CREATE: 'LEAD_CREATE', ... }
 */
function buildP(permissions) {
  return permissions.reduce((acc, perm) => {
    if (perm.code) acc[perm.code] = perm.code;
    return acc;
  }, {});
}

/**
 * ─────────────────────────────────────────────────────────────
 * invalidatePermissionsCache — CreatePermission save ke baad call karo
 * Taaki P object turant update ho jaye
 * ─────────────────────────────────────────────────────────────
 */
export function invalidatePermissionsCache() {
  localStorage.removeItem('allPermissions');
}

/**
 * ─────────────────────────────────────────────────────────────
 * DEBUG HELPER
 * Browser console mein:
 *   import { debugPermissions } from '../hooks/usePermissions';
 *   debugPermissions();
 * ─────────────────────────────────────────────────────────────
 */
export function debugPermissions() {
  const raw   = localStorage.getItem('userPermissions');
  const user  = localStorage.getItem('user');
  const cache = localStorage.getItem('allPermissions');

  console.group('🔐 Permission Debug');
  console.log('User role:', JSON.parse(user || '{}')?.role);
  console.log('Assigned codes:', JSON.parse(raw || '[]'));

  if (cache) {
    const perms = JSON.parse(cache);
    console.log(`Cached DB permissions: ${perms.length} total`);
    console.log('Codes:', perms.map(p => p.code));
  } else {
    console.warn('⚠️ No cached permissions — page reload pe fetch hoga');
  }
  console.groupEnd();
}